import Link from '@/routing/AppLink';
import type { ButtonHTMLAttributes } from 'react';

type VariantType = 'default' | 'ellipse';
type EmphasisType = 'light' | 'heavy';

interface ButtonIconProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: VariantType;
  emphasis?: EmphasisType;
  href?: string;
  target?: '_self' | '_blank';
  blurOnFocus?: boolean;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const BASE =
  'inline-flex cursor-pointer appearance-none items-center justify-center rounded-full border-none align-middle text-sm font-medium outline-none transition-[background-color,border-color,color,opacity] duration-300 ease-in-out disabled:pointer-events-none disabled:opacity-40 [&>div]:transition-[color,opacity,transform] [&>div]:duration-300 [&>div]:ease-in-out';

/** coreGrayBlack(#161617) @0.06 */
const VARIANT_CLASS: Record<VariantType, string> = {
  default:
    'min-h-10 min-w-10 rounded-lg [&>div]:rounded-lg [&>div]:p-1 hover:[@media(hover:hover)]:[&>div]:bg-[rgba(22,22,23,0.06)]',
  ellipse:
    'h-auto w-auto min-h-8 min-w-8 bg-apps-coreGrayBlack p-2 hover:[@media(hover:hover)]:opacity-100',
};

const EMPHASIS_CLASS: Record<string, string> = {
  'ellipse-light': 'opacity-80',
  'default-light': '[&_svg]:opacity-50',
};

export default function ButtonIcon({
  variant = 'default',
  emphasis = 'heavy',
  disabled = false,
  blurOnFocus = true,
  href,
  target,
  onClick,
  children,
  className,
  ...props
}: ButtonIconProps) {
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    // 사용자가 전달한 onClick 핸들러가 있다면 호출
    if (onClick) {
      onClick(event);
    }

    // blurOnFocus prop이 true일 경우 포커스 해제
    if (blurOnFocus) {
      event.currentTarget.blur();
    }
  };

  const button = (
    <button
      className={`${BASE} ${VARIANT_CLASS[variant]} ${
        EMPHASIS_CLASS[`${variant}-${emphasis}`] ?? ''
      } ${className ?? ''}`}
      disabled={disabled}
      onClick={handleClick}
      {...props}
    >
      <div>{children}</div>
    </button>
  );

  if (href && !disabled) {
    return (
      <Link href={href} target={target} prefetch={false}>
        {button}
      </Link>
    );
  }

  return button;
}
